import { Box, Circle, Icon, Text } from '@chakra-ui/react';
import assert from 'assert';
import _ from 'lodash';
import React from 'react';
import { IconType } from 'react-icons';

import { date as commonDate } from '@finley-technologies/common';

import { EventForRenderFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  getInsertedDeliverableStatusPayloadV1WithAssert,
  getUpdatedDeliverableStatusPayloadV1WithAssert,
  getSubmittedDeliverableSubmissionPayloadV1WithAssert,
} from 'packages/common-frontend/utils';
import { FiClock, FiEdit2, FiSend } from 'react-icons/fi';

const deliverableEvent: {
  [key: string]: {
    Component: (props: {
      event: EventForRenderFragmentGQLType;
    }) => React.ReactElement;
    icon: IconType;
  };
} = {
  insertedDeliverable: {
    Component: function InsertedDeliverableComponent({
      event,
    }: {
      event: EventForRenderFragmentGQLType;
    }) {
      const { actorName, targetName } = event;
      const payload = getInsertedDeliverableStatusPayloadV1WithAssert(event);
      const {
        deliverable: { creditFacilityName },
      } = payload;
      return (
        <Text>
          <strong>{actorName}</strong>
          {` surfaced a deliverable: `}
          <strong>{targetName}</strong>
          {` for `}
          <strong>{creditFacilityName}</strong>
        </Text>
      );
    },
    icon: FiClock,
  },
  updatedDeliverableStatus: {
    Component: function UpdatedDeliverableStatusComponent({
      event,
    }: {
      event: EventForRenderFragmentGQLType;
    }) {
      const { actorName, targetName } = event;
      const payload = getUpdatedDeliverableStatusPayloadV1WithAssert(event);
      const {
        deliverable: { creditFacilityName, status },
      } = payload;
      return (
        <Text>
          <strong>{actorName}</strong>
          {` updated `}
          <strong>{targetName}</strong>
          {` to `}
          <strong>{`${_.lowerCase(status)}`}</strong>
          {` for `}
          <strong>{creditFacilityName}</strong>
        </Text>
      );
    },
    icon: FiEdit2,
  },
  submittedDeliverableSubmission: {
    Component: function SubmittedDeliverableSubmission({
      event,
    }: {
      event: EventForRenderFragmentGQLType;
    }) {
      const { actorName, targetName } = event;
      const payload = getSubmittedDeliverableSubmissionPayloadV1WithAssert(
        event,
      );
      const {
        deliverable: { creditFacilityName },
      } = payload;
      return (
        <Text>
          <strong>{actorName}</strong>
          {` submitted `}
          <strong>{targetName}</strong>
          {` for `}
          <strong>{creditFacilityName}</strong>
        </Text>
      );
    },
    icon: FiSend,
  },
};

export const DeliverableEvents = ({ events }: { events: any[] }) => {
  return (
    <>
      {_.map(events, (event) => {
        const { actionKey, created_at: createdAt } = event;
        assert(
          _.isString(actionKey) && actionKey in deliverableEvent,
          `${actionKey} is an invalid action key for deliverables event`,
        );
        const { Component: DeliverableEventText } = deliverableEvent[actionKey];
        return (
          <Box
            alignContent="center"
            display="flex"
            key={`funding-request-event-${event.id}`}
          >
            <Circle size="40px" bg="millet.50">
              <Icon as={deliverableEvent[actionKey].icon} />
            </Circle>
            <Box padding="5px 10px">
              <DeliverableEventText event={event} />
              <Text fontSize="sm" color="gray" mb="10px">
                {commonDate.epochToLocaleDateString(createdAt)}
              </Text>
            </Box>
          </Box>
        );
      })}
    </>
  );
};
